import type { AnalyticsFilterOptions, AnalyticsSection } from '#shared/types';
import { api } from '#client/lib/api';
import { ENDPOINT } from '#client/lib/endpoints';
import { type KeyedRequest, useKeyedRequest } from '#client/hooks/useKeyedRequest';
import type { AnalyticsFilterState } from './useAnalyticsFilters';

/**
 * One section's whole payload, in one request.
 *
 * The key is the query itself, stringified — the convention `useWorklist`
 * already follows. That is what makes changing a filter refetch and re-rendering
 * for any other reason not.
 */
export function useAnalyticsSection<T>(
  section: AnalyticsSection,
  filters: AnalyticsFilterState,
): KeyedRequest<T> {
  const query = filters.query;

  return useKeyedRequest<T>(
    `${section}:${JSON.stringify(query)}`,
    (signal) => api.get<T>(ENDPOINT.analyticsSection(section), query, signal),
    `Could not load the ${section} figures`,
  );
}

/**
 * The option lists for the filter bar.
 *
 * Its own request, and keyed on nothing, so that moving the date range does not
 * refetch the entire test catalogue. It changes when the catalogue does, which
 * is not on the timescale of somebody clicking through a date picker.
 */
export function useAnalyticsFilterOptions(): KeyedRequest<AnalyticsFilterOptions> {
  return useKeyedRequest<AnalyticsFilterOptions>(
    'analytics-filters',
    (signal) => api.get<AnalyticsFilterOptions>(ENDPOINT.analyticsFilters, undefined, signal),
    'Could not load the filter options',
  );
}
